var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { ClampToEdgeWrapping, Color, Frustum, Group, LinearFilter, LinearMipMapLinearFilter, Mesh, Raycaster, RGBAFormat, Sphere, Vector3, WebGLRenderTarget, } from "three";
import { Cartographic, MathUtils, Rectangle } from "../../math";
import { DebugDraw } from "../../renderer/debug";
import { PreparingObject } from "../preparing_object";
import { EvictionQueue } from "../../utils/EvictionQueue";
import { PickingMeshBasicMaterial } from "../../materials";
import { GeometryCache } from "./GeometryCache";
import { TileCache } from "./TileCache";
import { QuadTreeNode } from "./QuadTreeNode";
import { OptionsManager } from "../../options";
import { ImageCache } from "./ImageCache";
import { TileRegionIterator } from "../../math/tiling/TileRegionIterator";
import { WebMercatorTilingScheme } from "../../math/tiling";
import { splitTile } from "./SplitTile";
/**
 * This object renders a true, world scale WGS84 globe.
 */
export class Globe extends PreparingObject {
    /**
     *
     * @param terrainProvider The object that will provide tile geometry for the globe
     * @param imageryProvider The specific imagery source this globe will use
     * @param tileCompositor The render pass that handles tile composition work
     * @param requestManager The request manager instance the globe will queue all its requests with
     * @param options
     */
    constructor(terrainProvider, imageryProvider, tileCompositor, requestManager, options) {
        var _a, _b;
        super();
        /**
         * The eviction queue for tiles to keep within the tile budget
         * @private
         */
        this.evictionQueue = new EvictionQueue();
        /**
         * QuadTree structure for the composite globe tiles
         * @private
         */
        this.tileTree = [];
        /**
         * Contains the intersection of the regions of all data providers. This is the valid region the globe can generate
         * tiles for.
         * @private
         */
        this.region = Rectangle.MAX;
        // Disable matrix auto update as we need to do it in prepareFrame to ensure we have the latest information
        this.matrixAutoUpdate = false;
        this.tileCompositor = tileCompositor;
        this.terrainProvider = terrainProvider;
        this.imageryProvider = imageryProvider;
        this.requestManager = requestManager;
        this._tileBudget = (_a = options === null || options === void 0 ? void 0 : options.tileBudget) !== null && _a !== void 0 ? _a : 1024; // TODO: investigate sane default
        this._tileSize = (_b = options === null || options === void 0 ? void 0 : options.tileTextureSize) !== null && _b !== void 0 ? _b : { x: 256, y: 256 };
        this.geometryCache = new GeometryCache(this.terrainProvider.tilingScheme);
        this.imageCache = new ImageCache(this.imageryProvider.tilingScheme);
        this.tileCache = new TileCache(this.terrainProvider.tilingScheme);
        if (!this.terrainProvider.region.intersects(this.imageryProvider.region)) {
            throw new Error("The geometry and imagery provider's regions do not intersect");
        }
        this.region = this.region.intersection(this.terrainProvider.region);
        this.region = this.region.intersection(this.imageryProvider.region);
        this.generateRootTiles();
    }
    get attributions() {
        return this.imageryProvider.attributions;
    }
    get ellipsoid() {
        return this.terrainProvider.tilingScheme.ellipsoid;
    }
    get tileSize() {
        return this._tileSize;
    }
    get tileBudget() {
        return this._tileBudget;
    }
    set tileBudget(value) {
        this._tileBudget = value;
    }
    /**
     * This function needs to be called once per-frame before rendering is performed. This handles converting the
     * quadtree of map tiles into a render list for ThreeJS to handle.
     */
    prepare(renderer, camera, frameData) {
        if (frameData.options.debug.imageryLayerBoundingVolumes &&
            typeof this.imageryProvider.debugDraw !== "undefined") {
            this.imageryProvider.debugDraw();
        }
        const screenSize = renderer.getSize();
        // Clear children
        this.clear();
        // Mark a new frame for the tile cache, making all nodes potentially evict-able.
        this.evictionQueue.newFrame();
        // Perform the matrix update now as we need the most up to date info we can get here.
        //
        // Then we need to calculate the modelViewMatrix for the object we can project the bounding spheres into clip
        // space and calculate screen area
        this.updateMatrix();
        const frustumCam = camera.getCurrentCamera();
        const frustumMatrix = frustumCam.projectionMatrix.clone().multiply(frustumCam.matrixWorldInverse);
        const camFrustum = new Frustum().setFromProjectionMatrix(frustumMatrix);
        // Need the position of the camera in world space for projecting screen radius of bounding spheres
        const camWorldPos = camera.getWorldPosition(new Vector3());
        const nodeStack = [...this.tileTree];
        while (nodeStack.length > 0) {
            const node = nodeStack.pop();
            const belowMinimumLevel = node.zoomLevel < this.imageryProvider.minLevel;
            const atMinLevel = node.zoomLevel === this.imageryProvider.minLevel;
            // If we haven't yet reached the minimum level then we can skip any processing at this node.
            // We should just continue to the next level
            if (belowMinimumLevel) {
                // Init the child nodes if we need to
                if (node.children.length === 0) {
                    node.generateChildren();
                }
                // Move the next level, we can't render anything until we reach the minimum level
                nodeStack.push(...node.children);
                continue;
            }
            // When at the minimum level we may be in a situation where the node data isn't resident yet. This will
            // spawn requests for them and block further traversal until they are loaded
            if (atMinLevel && node.loaded === undefined) {
                this.trySpawnTileLoad(node);
                continue;
            }
            // =====================
            // By this point we are now definitely traversing meaningful tiles, having skipped the 'pre minimum level'
            // tiles.
            //
            // If node.mesh is not present here then we have an internal bug. The 'minimum level' tiles are guaranteed
            // to be loaded by the stage above, any of their children should not be queued for traversal until node.mesh
            // is present. This is handled in 'handleChildren' which will wait until the tile is loaded.
            //
            // Violating this expectation is a bug
            if (node.loaded === undefined) {
                throw new Error("'node.mesh' is not present, but should be. This is an internal bug");
            }
            // Touch any node we traverse to keep it alive
            if (node.evictionNode !== undefined && node.lastTouchedFrame < frameData.frameIndex) {
                this.evictionQueue.touch(node.evictionNode);
                node.lastTouchedFrame = frameData.frameIndex;
            }
            // Transform the imagery sphere into world space
            const imagerySphereWorld = node.boundingSphere.clone();
            this.localToWorld(imagerySphereWorld.center);
            // Transform the mesh sphere into world space
            const meshSphereWorld = node.loaded.boundingSphere.clone();
            this.localToWorld(meshSphereWorld.center);
            // Apply frustum culling to the tile if the feature is enabled.
            if (frameData.options.debug.globeTileFrustumCulling) {
                const intersectsFrustum = camFrustum.intersectsSphere(meshSphereWorld);
                if (!intersectsFrustum) {
                    continue;
                }
            }
            const canRefine = this.terrainProvider.canRefine(node.x, node.y, node.zoomLevel);
            const qualityRequirementReached = this.checkSphereQualityMet(meshSphereWorld, camWorldPos, camera.perspective.fov, screenSize.y);
            // We only allow terminating here if we've reached the minimum zoom level of the dataset, otherwise we would
            // render nothing.
            //
            // We also call 'handleChildren', which will only be evaluated if the previous two conditions evaluate to
            // false. handleChildren will recurse to the next zoom level and queue the current node's children for
            // traversal. We rely on 'if' statement's short circuit behavior to ensure 'handleChildren' only runs if
            // the previous two conditions fail.
            if (!canRefine || qualityRequirementReached || !this.handleChildren(node, nodeStack, frameData)) {
                if (frameData.options.debug.imageryTileBoundingVolumes) {
                    DebugDraw.addSphere(imagerySphereWorld.center, imagerySphereWorld.radius);
                }
                if (frameData.options.debug.geometryTileBoundingVolumes) {
                    const color = [
                        node.loaded.material.color.r * 255,
                        node.loaded.material.color.g * 255,
                        node.loaded.material.color.b * 255,
                    ];
                    DebugDraw.addSphere(meshSphereWorld.center, meshSphereWorld.radius, color);
                }
                this.add(node.loaded.object);
            }
        }
        // Handle a tile eviction cycle after all visible tiles have been touched
        this.evictionQueue.evictNodes(() => {
            return this.evictionQueue.length > this.tileBudget;
        }, (node) => {
            // Evict the object's data
            this.evict(node.x, node.y, node.zoomLevel);
            delete node.loaded;
            delete node.evictionNode;
        });
    }
    /**
     * Calculate the true terrain height at a given point
     *
     * @param position
     * @returns
     */
    getTerrainHeight(position) {
        const terrainPoint = new Cartographic();
        const normal = this.ellipsoid.geodeticSurfaceNormalForCartographic(position);
        const origin = this.ellipsoid.cartesianForCartographic(position).addScaledVector(normal, 20000);
        // Get root tile node
        let node;
        for (const root of this.tileTree) {
            if (root.region.contains(position)) {
                node = root;
                break;
            }
        }
        if (node === undefined) {
            return 0;
        }
        // Find the most refined node with loaded geometry
        while (node.children.length > 0) {
            for (const child of node.children) {
                if (child.region.contains(position)) {
                    node = child;
                    break;
                }
            }
        }
        while (node.loaded === undefined && node.parent !== undefined) {
            node = node.parent;
        }
        if (node.loaded === undefined) {
            return 0;
        }
        // Run the raycast
        const caster = new Raycaster(origin, normal.negate(), 0);
        caster.firstHitOnly = true;
        const intersections = caster.intersectObject(node.loaded.object, true);
        if (intersections.length > 0) {
            this.ellipsoid.cartographicForPosition(intersections[0].point, terrainPoint);
            return terrainPoint.height;
        }
        return 0;
    }
    /**
     * Process the children of a node, spawning the request for a tile. Once all four children are
     * resident (tiles are loaded), we can push them onto the stack to be further refined.
     *
     * @param node Current node being processed
     * @param nodeStack Stack to push child nodes onto
     * @param frameData Frame data context
     * @returns True if the children have been pushed
     */
    handleChildren(node, nodeStack, frameData) {
        const childReachedMinLevel = node.zoomLevel + 1 >= this.imageryProvider.minLevel;
        // Init the child nodes if we need to
        if (node.children.length === 0) {
            node.generateChildren();
        }
        // Count how many of the child nodes are resident
        let nodesResident = 0;
        node.children.forEach((v) => {
            // Nodes are touched here to make sure we keep all 4 children alive, even if some children are outside the
            // frustum. This prevents evicting one of the children, preventing the other 3 from being rendered.
            if (node.evictionNode !== undefined && node.lastTouchedFrame < frameData.frameIndex) {
                this.evictionQueue.touch(node.evictionNode);
                node.lastTouchedFrame = frameData.frameIndex;
            }
            // We always want to recurse to a lower level if we haven't yet reached the minimum level
            if (v.loaded !== undefined || !childReachedMinLevel) {
                nodesResident += 1;
            }
            else {
                this.trySpawnTileLoad(v);
            }
        });
        if (nodesResident === 4) {
            nodeStack.push(...node.children);
            return true;
        }
        else {
            return false;
        }
    }
    /**
     * Test if the bounding sphere of a tile satisfies the pixel requirement for it be further refined. If the projected
     * diameter of the sphere is less than 2.25 times the size of an individual tile, then we can increase the level
     * of detail.
     *
     * This function determines to how close the camera has to be to the globe before it subdivides.
     *
     * @param sphere Relevant bounding sphere
     * @param cameraPos Camera position
     * @param fov Camera FOV
     * @param screenHeight Height of the render display target
     */
    checkSphereQualityMet(sphere, cameraPos, fov, screenHeight) {
        const maxTileDimension = Math.max(this.tileSize.x, this.tileSize.y);
        const distance = cameraPos.clone().sub(sphere.center).length();
        const pixelRadius = MathUtils.computeProjectedRadius(fov, distance, sphere.radius, screenHeight / 2);
        return 2 * pixelRadius <= 2.25 * maxTileDimension;
    }
    /**
     * Internal function that handles queueing a network request for a tile if the data is missing. It will *not* queue
     * a request if it finds one is already in flight.
     *
     * @param node
     * @private
     */
    trySpawnTileLoad(node) {
        const objectMissing = node.loaded === undefined;
        const requestMissing = node.request === undefined;
        const requestCancelled = node.request !== undefined && node.request.status === "cancelled";
        const requestComplete = node.request !== undefined && node.request.status === "complete";
        // Keep trying as long as the object isn't there and the request isn't in flight
        if (objectMissing && (requestMissing || requestCancelled || requestComplete)) {
            const taskFn = () => __awaiter(this, void 0, void 0, function* () {
                const tile = yield this.loadTile(node.x, node.y, node.zoomLevel, node.parent);
                if (tile !== undefined) {
                    node.loaded = tile;
                    node.evictionNode = this.evictionQueue.add(node);
                }
            });
            node.request = this.requestManager.queueRequest("globe", taskFn);
        }
    }
    /**
     * Returns a ThreeJS 'Mesh' object for the requested tile.
     *
     * This will return 'undefined' if the tile was deemed superfluous. Some imagery sources may return textures below
     * the requested zoomLevel. If all textures are below 'zoomLevel' then the tile has no reason to exist as the parent
     * tile would provide identical quality while using less VRAM.
     *
     * There are multiple levels of caching used here:
     *      - Geometry and imagery tiles are cached individually
     *      - Final composited globe tiles are cached
     *
     * @param x The x coordinate of the tile to generate
     * @param y The y coordinate of the tile to generate
     * @param zoomLevel The zoom level we're generating a tile at
     * @param parent Parent tree node to subdivide if no geometry is available at the current zoom level
     * @private
     */
    loadTile(x, y, zoomLevel, parent) {
        return __awaiter(this, void 0, void 0, function* () {
            // Check to see if we can skip loading by returning a cached result
            let tile = this.tileCache.acquire(x, y, zoomLevel);
            if (tile !== undefined) {
                return tile;
            }
            const geometryItems = yield this.getOrLoadTerrainTile(x, y, zoomLevel, parent);
            if (geometryItems.length === 0)
                return undefined;
            let textures;
            if (this.terrainProvider.tilingScheme.constructor === this.imageryProvider.tilingScheme.constructor) {
                // If the imagery and geometry tiling schemes are the same then we already know the exact tile to load
                // imagery for.
                textures = yield this.getOrLoadImageryTile(x, y, zoomLevel);
            }
            else {
                // Iterator sequence that will yield all imagery tiles needed to cover this tile
                const geometryTileRectangle = this.terrainProvider.tilingScheme
                    .tileXYToRectangle(x, y, zoomLevel)
                    .intersection(this.region);
                const overlappingImageTiles = new TileRegionIterator(this.imageryProvider.tilingScheme, geometryTileRectangle, zoomLevel);
                textures = [];
                for (const v of overlappingImageTiles) {
                    const tile = yield this.getOrLoadImageryTile(v.x, v.y, zoomLevel);
                    textures.push(...tile);
                }
            }
            // If all the tiles are below the requested zoom level then we shouldn't make a new tile. In this case there is
            // no higher resolution data to display, so we just consume more VRAM to display the same data.
            const numTilesLowerRes = textures.filter((v) => v.zoomLevel < zoomLevel).length;
            if (numTilesLowerRes === textures.length) {
                return undefined;
            }
            // Perform the tile composition and mesh generation
            tile = this.createTileFromGeometryAndTextures(x, y, zoomLevel, geometryItems, textures);
            return this.tileCache.addItem(x, y, zoomLevel, tile);
        });
    }
    /**
     * Internal function used for getting tile imagery. Transparently handles a cache lookup + fetch if the data is
     * uncached.
     *
     * If no geometry can be found for the current zoom level, the tile's parent is subdivded and the appropriate
     * quadrant is used.
     *
     * @param x
     * @param y
     * @param zoomLevel
     * @param parent
     * @private
     */
    getOrLoadTerrainTile(x, y, zoomLevel, parent) {
        return __awaiter(this, void 0, void 0, function* () {
            // Try fetching geometry from cache
            let geometry = this.geometryCache.acquire(x, y, zoomLevel);
            if (geometry === undefined) {
                // Cache miss, spawn a request to fetch the relevant geometry tiles
                geometry = yield this.terrainProvider.loadTile(x, y, zoomLevel);
                // No geometry available for this zoom level, subdivide the parent
                if (geometry.length === 0 && parent !== undefined) {
                    const parentTiles = this.geometryCache.acquire(parent.x, parent.y, parent.zoomLevel);
                    for (const tile of parentTiles) {
                        const split = splitTile(tile, x, y, parent, this.terrainProvider.tilingScheme, this.region);
                        geometry.push(split);
                    }
                }
                geometry = this.geometryCache.addItem(x, y, zoomLevel, geometry);
            }
            return geometry;
        });
    }
    /**
     * Internal function used for getting tile imagery. Transparently handles a cache lookup + fetch if the data is
     * uncached.
     *
     * @param x
     * @param y
     * @param zoomLevel
     * @private
     */
    getOrLoadImageryTile(x, y, zoomLevel) {
        return __awaiter(this, void 0, void 0, function* () {
            // Try fetching imagery from cache
            let imagery = this.imageCache.acquire(x, y, zoomLevel);
            if (imagery === undefined) {
                // Cache miss, spawn a request to fetch the relevant imagery tiles
                imagery = yield this.imageryProvider.loadTile(x, y, zoomLevel);
                imagery = this.imageCache.addItem(x, y, zoomLevel, imagery);
            }
            return imagery;
        });
    }
    /**
     * This function will evict the cached data for the given tile. If there is no cache entry for the tile then the
     * function will just do nothing.
     *
     * @param zoomLevel The zoom level we're looking up a tile to evict
     * @param x The x coordinate of the tile to evict
     * @param y The y coordinate of the tile to evict
     */
    evict(x, y, zoomLevel) {
        this.tileCache.release(x, y, zoomLevel);
        this.geometryCache.release(x, y, zoomLevel);
        this.imageCache.release(x, y, zoomLevel);
    }
    /**
     * An internal function that handles taking a ThreeJS texture and emiting a Mesh instance into the tile cache that
     * corresponds to that tile.
     *
     * @param geometry The geometry to apply
     * @param textures The textures to apply
     * @param zoomLevel Zoom level of the tile
     * @param x Tile x coordinate
     * @param y Tile y coordinate
     * @private
     */
    createTileFromGeometryAndTextures(x, y, zoomLevel, geometry, textures) {
        // An optimization to skip compositing when there is only a single texture that fills the tile
        let finalTexture;
        const sameTileScheme = this.imageryProvider.tilingScheme.constructor === this.terrainProvider.tilingScheme.constructor;
        if (textures.length === 1 && textures[0].zoomLevel === zoomLevel && sameTileScheme) {
            finalTexture = textures[0].texture;
            finalTexture.wrapS = ClampToEdgeWrapping;
            finalTexture.wrapT = ClampToEdgeWrapping;
            finalTexture.anisotropy = 4;
            finalTexture.minFilter = LinearMipMapLinearFilter;
            finalTexture.magFilter = LinearFilter;
            finalTexture.generateMipmaps = true;
        }
        else {
            const finalRT = new WebGLRenderTarget(this.tileSize.x, this.tileSize.y, {
                wrapS: ClampToEdgeWrapping,
                wrapT: ClampToEdgeWrapping,
                anisotropy: 4,
                minFilter: LinearMipMapLinearFilter,
                magFilter: LinearFilter,
                generateMipmaps: true,
                format: RGBAFormat,
            });
            for (const texture of textures) {
                this.emitCompositorJob(x, y, zoomLevel, finalRT, texture);
            }
            finalTexture = finalRT.texture;
        }
        const params = {
            map: finalTexture,
        };
        if (OptionsManager.DEBUG_COLOR_IMAGERY_TILES_BY_ZOOM_LEVEL) {
            params.color = depthColour(zoomLevel);
        }
        else if (OptionsManager.DEBUG_COLOR_IMAGERY_TILES_RANDOMLY) {
            params.color = new Color().setHSL(Math.random(), 1, 0.5);
        }
        const material = new PickingMeshBasicMaterial(params);
        const object = new Group();
        const boundingSphere = new Sphere();
        geometry.forEach((item) => {
            // Build the base mesh
            const mesh = new Mesh(item.geometry, material);
            mesh.position.copy(item.midpoint);
            mesh.frustumCulled = false;
            // Build the skirt mesh
            const skirtMesh = new Mesh(item.skirtGeometry, material);
            skirtMesh.position.copy(item.midpoint);
            mesh.frustumCulled = false;
            // Add the base mesh and skirt to the object
            object.add(mesh);
            object.add(skirtMesh);
            // Expand out all-encompassing bounding sphere to include the base mesh
            const tmpSphere = new Sphere();
            tmpSphere.copy(mesh.geometry.boundingSphere).center.add(mesh.position);
            if (boundingSphere.isEmpty()) {
                boundingSphere.copy(tmpSphere);
            }
            else {
                boundingSphere.union(tmpSphere);
            }
        });
        return {
            object,
            material,
            boundingSphere,
        };
    }
    /**
     * Emits a composition job to place src into dst.
     *
     * @param x Tile X coordinate we're compositing for
     * @param y Tile Y coordinate we're compositing for
     * @param zoomLevel Tile zoom level we're compositing for
     * @param dst The texture we're compositing into
     * @param src The texture we're compositing onto 'dst'
     * @private
     */
    emitCompositorJob(x, y, zoomLevel, dst, src) {
        src.texture.wrapS = ClampToEdgeWrapping;
        src.texture.wrapT = ClampToEdgeWrapping;
        // Calculate the region of the destination tile in geographic coordinates
        const destRect = this.terrainProvider.tilingScheme.tileXYToRectangle(x, y, zoomLevel);
        const destW = destRect.width;
        const destH = destRect.height;
        // Calculate the region of the source tile in geographic coordinates
        const srcRect = this.imageryProvider.tilingScheme.tileXYToRectangle(src.x, src.y, src.zoomLevel);
        const srcW = srcRect.width;
        const srcH = srcRect.height;
        // Calculate the offset from dest to src
        const offset = {
            x: srcRect.west - destRect.west,
            y: srcRect.south - destRect.south,
        };
        const scale = {
            x: srcW / destW,
            y: srcH / destH,
        };
        const scaledOffset = {
            x: offset.x / destW,
            y: offset.y / destH,
        };
        const reprojectWebMercator = this.imageryProvider.tilingScheme instanceof WebMercatorTilingScheme &&
            !(this.terrainProvider.tilingScheme instanceof WebMercatorTilingScheme);
        this.tileCompositor.pushJobs({
            disposeSrc: true,
            src: src.texture,
            dst,
            scale,
            offset: scaledOffset,
            rectangle: destRect,
            reprojectWebMercator,
        });
    }
    /**
     * Internal function used in the constructor for initializing the 'rootTiles' array.
     *
     * @private
     */
    generateRootTiles() {
        // Generate the root tiles, and queue them for streaming.
        //
        // Some tiling schemes are not pure quad-trees. Some tiling schemes (usually with an equirectangular projection)
        // have, for example, two root nodes along the x-axis. A visual example:
        //
        // == Level 0 ==
        // ┌───────┬───────┐
        // │       │       │
        // │       │       │
        // │       │       │
        // └───────┴───────┘
        // == Level 1 ==
        // ┌───┬───┬───┬───┐
        // │   │   │   │   │
        // ├───┼───┼───┼───┤
        // │   │   │   │   │
        // └───┴───┴───┴───┘
        //
        // The resulting tree structure is almost a quad-tree. This isn't much trouble to deal with, but does mean we
        // need an array of root nodes.
        const numX = this.terrainProvider.tilingScheme.getNumberOfXTilesAtLevel(0);
        const numY = this.terrainProvider.tilingScheme.getNumberOfYTilesAtLevel(0);
        for (let y = 0; y < numY; y += 1) {
            for (let x = 0; x < numX; x += 1) {
                const newRoot = new QuadTreeNode(x, y, 0, this.terrainProvider.tilingScheme, this.ellipsoid);
                this.tileTree.push(newRoot);
            }
        }
    }
}
/**
 * Debug function for mapping zoomLevel to a repeating of sequence of bright colours. The intended use is for debug
 * visualization of the zoom level of tiles.
 * @param zoomLevel
 * @private
 */
function depthColour(zoomLevel) {
    switch (zoomLevel % 6) {
        case 0:
            return new Color(1, 0, 0);
        case 1:
            return new Color(0, 1, 0);
        case 2:
            return new Color(0, 0, 1);
        case 3:
            return new Color(1, 0, 1);
        case 4:
            return new Color(0, 1, 1);
        case 5:
            return new Color(1, 1, 0);
        default:
            return new Color(1, 1, 1);
    }
}
